import React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';

export type MapTextFieldProps = Omit<TextFieldProps, 'variant'>;

export const mapInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: '1.5px' },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
    '&.Mui-disabled': { bgcolor: 'action.hover' },
  },
  '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input:not(.MuiAutocomplete-input)': {
    py: '8px',
    height: '20px',
    lineHeight: '20px',
  },
  '& .MuiOutlinedInput-input:not(.MuiInputBase-inputAdornedStart):not(.MuiAutocomplete-input)': { pl: '12px' },
  '& .MuiOutlinedInput-input:not(.MuiInputBase-inputAdornedEnd):not(.MuiAutocomplete-input)': { pr: '12px' },
  '& .MuiInputBase-multiline': { py: '8px', px: '12px' },
  '& .MuiInputBase-multiline .MuiOutlinedInput-input': { px: 0 },
  '& .MuiInputBase-input::placeholder': { color: 'text.disabled', opacity: 1 },
  '& .MuiFormHelperText-root': { mx: 0, mt: '6px', fontSize: 12, lineHeight: '16px' },
} as const;

const topLabelSx = {
  '& .MuiInputLabel-root': {
    position: 'relative',
    transform: 'none',
    mb: '6px',
    maxWidth: '100%',
    fontSize: 13,
    lineHeight: '20px',
    fontWeight: 500,
    color: 'text.primary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pointerEvents: 'auto',
    '&.Mui-focused': { color: 'text.primary' },
    '&.Mui-error': { color: 'error.main' },
    '&.Mui-disabled': { color: 'text.disabled' },
    '& .MuiFormLabel-asterisk': { color: 'primary.main' },
  },
  '& .MuiOutlinedInput-notchedOutline > legend': { maxWidth: '0.01px' },
} as const;

const MapTextField = React.forwardRef<HTMLInputElement, MapTextFieldProps>(
  ({ size = 'small', fullWidth = true, sx, label, slotProps, InputLabelProps, ...props }, ref) => {
    const mergedSlotProps = label
      ? {
          ...slotProps,
          inputLabel: {
            shrink: true,
            ...(InputLabelProps as object),
            ...(slotProps?.inputLabel as object),
          },
        }
      : slotProps;
    return (
      <TextField
        inputRef={ref}
        variant="outlined"
        size={size}
        fullWidth={fullWidth}
        label={label}
        slotProps={mergedSlotProps}
        InputLabelProps={label ? undefined : InputLabelProps}
        sx={{
          ...mapInputSx,
          ...(label ? topLabelSx : {}),
          ...sx,
        }}
        {...props}
      />
    );
  }
);

MapTextField.displayName = 'MapTextField';
export default MapTextField;
